/**
 * Sheet „Zahlung erfassen" (`payment-sheet`, Spec 04b, „Sheet Zahlung
 * erfassen"). Drei Einstiege aus der Spec, zwei davon in 04b gebaut:
 *
 * - **Aus einem offenen Posten** (`prefill` gesetzt) — Mieter steht fest
 *   (`payment-create-tenant` entfällt), Betrag mit dem offenen Rest
 *   vorbelegt, eine Zuordnung auf genau diesen Posten ist gesetzt.
 * - **Ohne Bezug** (`prefill === null`, von `payment-add` im Bereich *Geld* —
 *   bis Spec 07 vom inzwischen entfallenen Bildschirm „Heute", AK-456) —
 *   Mieter wird im Sheet gewählt, dann aus dessen offenen Posten verteilt.
 * - **Aus den Klärfällen** (`unallocatedEntry` gesetzt, Spec 04c, „Reiter
 *   Klärfälle") — Kopfdaten (Mieter, Betrag, Datum, Zahlweise,
 *   Verwendungszweck) stehen fest, unbedienbar; darunter dieselbe
 *   Zuordnungsliste wie in den anderen beiden Einstiegen. Gespeichert wird
 *   über `POST /payments/{id}/allocations` (`useAddAllocations`) statt über
 *   `POST /payments` — **keine neue Zahlung**, nur eine ergänzte Zuordnung
 *   (I7). Genau die im Kopfkommentar vor 04c gemachte Zusage: „ohne Umbau
 *   der Sheet-Mechanik", nur ein zweiter Vorbelegungsmodus.
 *
 * **Die einzige Rechnung im Client** (Spec 04b, „Sheet Zahlung erfassen"):
 * „Nicht zugeordnet" = Zahlbetrag minus der eingetippten Zuordnungen, auf
 * ganzen Cent, ohne Rundung. Der Restbetrag je Posten (`open_cents`) kommt
 * vollständig vom Server (I9) — dieses Sheet entscheidet nichts über Status
 * oder Fälligkeit. Im dritten Einstieg ist der „Zahlbetrag" dieser Rechnung
 * der Rest der Zahlung (`unallocatedEntry.unallocatedCents`), nicht ihr
 * voller Betrag — der ist bereits ganz oder teilweise verteilt.
 */

import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { formatDate, todayInBerlin } from '@/lib/date'
import { centsToDecimalInput, formatCents, parseCents, remainingCents } from '@/lib/money'
import { useAddAllocations, useCreatePayment, useOpenItems, useUpcomingMonths } from '@/features/billing/api'
import type { PaymentCreate } from '@/features/billing/api'
import { FormField, FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { PaymentAllocations } from '@/features/billing/PaymentAllocations'
import { PaymentMethodField } from '@/features/billing/PaymentMethodField'
import { PaymentReservations } from '@/features/billing/PaymentReservations'
import type { PaymentReservationSelection } from '@/features/billing/PaymentReservations'
import { PaymentTenantField } from '@/features/billing/PaymentTenantField'
import type { PaymentTenantSelection } from '@/features/billing/PaymentTenantField'

export interface PaymentSheetPrefill {
  readonly tenantId: string
  readonly tenantLabel: string
  /** `null`, wenn die Kachel der Zahlungsmatrix mehr als eine offene
   * Sollstellung bündelt (Spec 08, Fachregel 5, AK-478/AK-487) — dann trägt
   * das Sheet **keine** vorbelegte Zuordnung, verteilt wird in
   * `PaymentAllocations`. Aus der Liste offener Posten (Spec 04b) kommt hier
   * immer ein `string`, weil ein Posten dort für genau eine Sollstellung
   * steht. */
  readonly chargeId: string | null
  readonly openCents: number
}

/** Der dritte Einstieg „aus den Klärfällen" (Spec 04c) — eine bereits
 * bestehende Zahlung mit Rest, deren Kopfdaten feststehen. */
export interface PaymentSheetUnallocatedEntry {
  readonly paymentId: string
  readonly tenantId: string
  readonly tenantLabel: string
  readonly amountDisplay: string
  readonly receivedOn: string
  readonly methodLabel: string
  readonly reference: string | null
  /** Der noch nicht zugeordnete Rest dieser Zahlung — der „Zahlbetrag" der
   * einzigen Client-Rechnung in diesem Einstieg (siehe Kopfkommentar). */
  readonly unallocatedCents: number
}

interface PaymentSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /** `null` → Einstieg „ohne Bezug". Gesetzt → Einstieg „aus einem offenen
   * Posten" (Spec 04b, „Sheet Zahlung erfassen"). Wird ignoriert, sobald
   * `unallocatedEntry` gesetzt ist. */
  readonly prefill: PaymentSheetPrefill | null
  /** Gesetzt → dritter Einstieg „aus den Klärfällen" (Spec 04c). Optional statt
   * `| null` als Pflichtprop: Der bestehende Aufrufer im Reiter *Offene Posten*
   * (`routes/Geld.tsx`) braucht diesen Einstieg nie und soll ihn nicht bei jeder
   * Änderung nachtragen müssen. */
  readonly unallocatedEntry?: PaymentSheetUnallocatedEntry | null
  /** Ruft mit dem Bestätigungstext, sobald gespeichert ist — der Streifen
   * lebt außerhalb dieses Sheets weiter (Spec 04b: „Sheet schließt … Ein
   * Streifen unten bestätigt"), deshalb hier nur die Meldung nach oben. */
  readonly onSaved: (message: string) => void
}

interface FormValues {
  readonly amount: string
  readonly receivedOn: string
  readonly method: string
  readonly reference: string
}

/** Der heutige Tag als `date`-Eingabewert (Spec 04b: „Datum … vorbelegt mit
 * heute"). **Nicht** die lokale Zeitzone des Geräts — `todayInBerlin()`
 * leitet denselben Kalendertag ab wie der Server, gegen den `received_on`
 * geprüft wird (Befund W5/B3, siehe `lib/date.ts`). */
function defaultReceivedOn(): string {
  return todayInBerlin()
}

function emptyForm(): FormValues {
  return { amount: '', receivedOn: defaultReceivedOn(), method: 'transfer', reference: '' }
}

interface ParsedAllocation {
  readonly chargeId: string
  readonly cents: number
}

export function PaymentSheet({ open, onOpenChange, prefill, unallocatedEntry, onSaved }: PaymentSheetProps) {
  const createMutation = useCreatePayment()
  const addAllocationsMutation = useAddAllocations()
  const amountRef = useRef<HTMLInputElement>(null)

  // `entry` normalisiert die optionale Prop auf `| null` — ab hier trägt
  // dieselbe Konstante die TS-Narrowing für den ganzen Komponentenkörper
  // („Control Flow Analysis of Aliased Conditions" funktioniert nur über eine
  // stabile Konstante, nicht über eine erneut ausgewertete Ternäre).
  const entry = unallocatedEntry ?? null

  const [tenant, setTenant] = useState<PaymentTenantSelection | null>(
    entry !== null
      ? { id: entry.tenantId, label: entry.tenantLabel }
      : prefill !== null
        ? { id: prefill.tenantId, label: prefill.tenantLabel }
        : null,
  )
  const [form, setForm] = useState<FormValues>(emptyForm())
  const [allocationAmounts, setAllocationAmounts] = useState<Readonly<Record<string, string>>>({})
  const [reservations, setReservations] = useState<Readonly<Record<string, PaymentReservationSelection>>>({})
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen beginnt frisch, vorbelegt aus `entry` bzw. `prefill` —
  // dasselbe Muster wie `ContractCreateSheet` (React „Adjusting state when a
  // prop changes", während des Renderns statt in einem Effekt).
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      if (entry !== null) {
        setTenant({ id: entry.tenantId, label: entry.tenantLabel })
        setForm(emptyForm())
        setAllocationAmounts({})
        setReservations({})
      } else {
        setTenant(prefill !== null ? { id: prefill.tenantId, label: prefill.tenantLabel } : null)
        setForm({ ...emptyForm(), amount: prefill !== null ? centsToDecimalInput(prefill.openCents) : '' })
        // Ohne `chargeId` (Kachel mit mehreren offenen Sollstellungen, Spec
        // 08) bleibt die Zuordnung leer — verteilt wird unten in
        // `PaymentAllocations`, nicht vorbelegt (AK-487).
        setAllocationAmounts(
          prefill !== null && prefill.chargeId !== null
            ? { [prefill.chargeId]: centsToDecimalInput(prefill.openCents) }
            : {},
        )
        setReservations({})
      }
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  const allocationsQuery = useOpenItems(
    { ...(tenant !== null ? { tenantId: tenant.id } : {}) },
    { enabled: tenant !== null },
  )
  const reservationsQuery = useUpcomingMonths(tenant?.id ?? '', { enabled: tenant !== null && entry === null })

  // Im dritten Einstieg ist der „Zahlbetrag" der noch nicht zugeordnete Rest
  // der bestehenden Zahlung, fest vom Server — kein Feld, aus dem geparst
  // würde (siehe Kopfkommentar, „Die einzige Rechnung im Client").
  const parsedAmount = entry !== null ? entry.unallocatedCents : parseCents(form.amount)

  let hasInvalidAllocation = false
  const parsedAllocations: ParsedAllocation[] = []
  for (const [chargeId, raw] of Object.entries(allocationAmounts)) {
    if (raw.trim() === '') continue
    const cents = parseCents(raw)
    if (cents === null || cents <= 0) {
      hasInvalidAllocation = true
      continue
    }
    parsedAllocations.push({ chargeId, cents })
  }
  // Parameter absichtlich nicht `entry` genannt — das würde die äußere
  // Konstante (den dritten Sheet-Einstieg) im Callback verdecken.
  const allocatedSum = parsedAllocations.reduce((sum, allocation) => sum + allocation.cents, 0)
  const selectedReservations = Object.values(reservations)
  const reservedSum = selectedReservations.reduce((sum, reservation) => sum + reservation.amountCents, 0)
  const unallocatedCents = remainingCents(parsedAmount, allocatedSum, reservedSum) ?? 0
  const isOverAllocated = unallocatedCents < 0

  const canSubmit =
    entry !== null
      ? // Eine ergänzte Zuordnung ohne jede Zeile wäre ein No-Op auf einer
        // echten Zahlung — anders als im ersten/zweiten Einstieg (dort ist
        // eine Vorauszahlung ganz ohne Zuordnung ein gültiges Ergebnis).
        parsedAllocations.length > 0 && !hasInvalidAllocation && !isOverAllocated
      : tenant !== null &&
        parsedAmount !== null &&
        parsedAmount > 0 &&
        form.receivedOn !== '' &&
        !hasInvalidAllocation &&
        !isOverAllocated

  function handleAllocationChange(chargeId: string, value: string) {
    setAllocationAmounts((current) => ({ ...current, [chargeId]: value }))
  }

  function handleAllocationFill(chargeId: string, openCents: number) {
    setAllocationAmounts((current) => ({ ...current, [chargeId]: centsToDecimalInput(openCents) }))
  }

  function toggleReservation(selection: PaymentReservationSelection) {
    const key = `${selection.contractId}-${selection.periodMonth}`
    setReservations((current) => {
      if (current[key] !== undefined) {
        return Object.fromEntries(Object.entries(current).filter(([currentKey]) => currentKey !== key))
      }
      return { ...current, [key]: selection }
    })
  }

  function applyError(error: unknown) {
    if (error instanceof ApiError && error.field !== null) {
      setFieldError({ field: error.field, message: error.message })
    } else if (error instanceof ApiError) {
      setGeneralError(error.message)
    } else {
      setGeneralError('Die Verbindung zum Server ist unterbrochen.')
    }
  }

  async function submit() {
    if (!canSubmit) return
    setFieldError(undefined)
    setGeneralError(undefined)

    if (entry !== null) {
      try {
        await addAllocationsMutation.mutateAsync({
          paymentId: entry.paymentId,
          body: {
            allocations: parsedAllocations.map((allocation) => ({
              charge_id: allocation.chargeId,
              amount_cents: allocation.cents,
            })),
          },
        })
        onOpenChange(false)
        onSaved('Zuordnung gespeichert.')
      } catch (error) {
        applyError(error)
      }
      return
    }

    // `canSubmit` ist im `else`-Zweig oben eine aliasierte Bedingung (TS
    // „Control Flow Analysis of Aliased Conditions"), die schon `tenant` und
    // `parsedAmount` als nicht-`null` belegt — die erneuten Checks hier sind
    // trotzdem nötig, weil TS diese Narrowing nicht über den `if (entry !==
    // null) return` hinweg in diese Funktion hinein trägt (zwei getrennte
    // Ausdrücke, keine einzige `&&`-Kette).
    if (tenant === null || parsedAmount === null || parsedAmount <= 0) return

    const referenceTrimmed = form.reference.trim()
    try {
      await createMutation.mutateAsync({
        body: {
          tenant_id: tenant.id,
          received_on: form.receivedOn,
          amount_cents: parsedAmount,
          // `PaymentCreate['method']` ist der enge Enum-Typ aus
          // `api-types.ts` — `PaymentMethodField` liefert nur dessen Werte
          // zurück, ein `as` bricht hier nichts Reales auf.
          method: form.method as PaymentCreate['method'],
          ...(referenceTrimmed !== '' ? { reference: referenceTrimmed } : {}),
          allocations: parsedAllocations.map((allocation) => ({
            charge_id: allocation.chargeId,
            amount_cents: allocation.cents,
          })),
          ...(selectedReservations.length > 0 ? {
            reservations: selectedReservations.map((reservation) => ({
              contract_id: reservation.contractId,
              period_month: reservation.periodMonth,
              amount_cents: reservation.amountCents,
            })),
          } : {}),
        },
      })
      onOpenChange(false)
      onSaved('Zahlung erfasst.')
    } catch (error) {
      applyError(error)
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit()
  }

  // Der Fehlerpfad für einen Zuordnungsfehler (`allocations[n].…`) passt zu
  // keinem festen `apiField` — die Spec sieht dafür `payment-create-error-
  // <feld>` mit dem wörtlichen Feldnamen vor (Spec 04b, „Sheet Zahlung
  // erfassen").
  const allocationError = fieldError?.field.startsWith('allocations') === true ? fieldError : undefined
  const reservationError = fieldError?.field.startsWith('reservations') === true ? fieldError : undefined

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          amountRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form data-testid="payment-sheet" onSubmit={(event) => void handleSubmit(event)} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              {entry !== null ? 'Zuordnung speichern' : 'Zahlung erfassen'}
            </SheetTitle>
            {prefill !== null && entry === null && <p className="text-label text-muted">{prefill.tenantLabel}</p>}
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            {entry !== null ? (
              // Kopfdaten stehen fest und sind nicht bedienbar (Spec 04c,
              // „Reiter Klärfälle") — reiner Text statt Formularfelder, damit
              // niemand versucht, hier etwas zu ändern, das gar nicht diesen
              // Endpunkt trifft (`POST .../allocations` kennt keinen Betrag,
              // kein Datum, keine Zahlweise).
              <div data-testid="payment-sheet-summary" className="border-border rounded-card flex flex-col gap-0.5 border px-3 py-2">
                <span className="text-body text-text font-medium">{entry.tenantLabel}</span>
                <span data-numeric className="text-label text-muted">
                  {entry.amountDisplay} · {formatDate(entry.receivedOn)} · {entry.methodLabel}
                </span>
                {entry.reference !== null && entry.reference !== '' && (
                  <span className="text-label text-muted">{entry.reference}</span>
                )}
              </div>
            ) : (
              <>
                {prefill === null && (
                  <PaymentTenantField
                    value={tenant}
                    onSelect={(next) => {
                      setTenant(next)
                      // Ein Mieterwechsel verwirft die bisherigen Zuordnungen —
                      // sie gehören zu den offenen Posten des vorherigen Mieters,
                      // die `PaymentAllocations` danach gar nicht mehr zeichnet
                      // (Befund B1, `reports/11-gatekeeper.md`). iOS macht das
                      // bereits so (`PaymentCreateViewModel.selectTenant`).
                      setAllocationAmounts({})
                      setReservations({})
                    }}
                    error={fieldError}
                  />
                )}

                <FormField
                  label="Betrag (in Euro)"
                  testId="payment-create-amount"
                  apiField="amount_cents"
                  errorPrefix="payment-create"
                  inputMode="decimal"
                  required
                  value={form.amount}
                  onChange={(value) => {
                    setForm((current) => ({ ...current, amount: value }))
                  }}
                  error={fieldError}
                  ref={amountRef}
                />

                <FormField
                  label="Datum"
                  testId="payment-create-received-on"
                  apiField="received_on"
                  errorPrefix="payment-create"
                  type="date"
                  required
                  value={form.receivedOn}
                  onChange={(value) => {
                    setForm((current) => ({ ...current, receivedOn: value }))
                  }}
                  error={fieldError}
                />

                <PaymentMethodField
                  value={form.method}
                  onChange={(value) => {
                    setForm((current) => ({ ...current, method: value }))
                  }}
                  error={fieldError}
                />

                <FormField
                  label="Verwendungszweck"
                  testId="payment-create-reference"
                  apiField="reference"
                  errorPrefix="payment-create"
                  value={form.reference}
                  onChange={(value) => {
                    setForm((current) => ({ ...current, reference: value }))
                  }}
                  error={fieldError}
                />
              </>
            )}

            {tenant !== null && (
              <>
                <PaymentAllocations
                  query={allocationsQuery}
                  amounts={allocationAmounts}
                  onAmountChange={handleAllocationChange}
                  onFill={handleAllocationFill}
                />

                {allocationError !== undefined && (
                  <p
                    data-testid={`payment-create-error-${allocationError.field}`}
                    role="alert"
                    className="text-label text-status-overdue"
                  >
                    {allocationError.message}
                  </p>
                )}

                {entry === null && (
                  <>
                    <PaymentReservations query={reservationsQuery} selections={reservations} onToggle={toggleReservation} />
                    {reservationError !== undefined && (
                      <p
                        data-testid={`payment-create-error-${reservationError.field}`}
                        role="alert"
                        className="text-label text-status-overdue"
                      >
                        {reservationError.message}
                      </p>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="payment-create-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            {/* Im Footer, nicht im scrollenden Rumpf: Bei 380 px mit fünf
             * Zuordnungszeilen lag die Zeile sonst unter der Falz, während
             * der gesperrte Speichern-Knopf sichtbar blieb — ein toter Knopf
             * ohne erkennbaren Grund (Befund DK1/K1,
             * `reports/12-design-reviewer.md`). Hier steht sie neben dem
             * Knopf, den sie erklärt, in beiden Breiten. */}
            {tenant !== null && (
              <div className="flex flex-col gap-0.5">
                <p
                  data-testid="payment-unallocated"
                  data-numeric
                  aria-live="polite"
                  className={`text-body font-medium ${isOverAllocated ? 'text-status-overdue' : 'text-muted'}`}
                >
                  Nicht zugeordnet: {formatCents(unallocatedCents)}
                </p>
                {reservedSum > 0 && (
                  <p data-testid="payment-reserved" data-numeric className="text-label text-muted">
                    Vorgemerkt: {formatCents(reservedSum)}
                  </p>
                )}
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="payment-create-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="submit"
                data-testid="payment-create-save"
                disabled={!canSubmit || createMutation.isPending || addAllocationsMutation.isPending}
                aria-busy={createMutation.isPending || addAllocationsMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                {entry !== null ? 'Zuordnung speichern' : 'Zahlung erfassen'}
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
